/**
 * Pure logic for the wet-hands swipe deck (SWIPE-01, LABEL-03, D-07/D-08/D-15).
 *
 * The on-device GESTURE (pointer events) and the `haptic('selection')` pulse are
 * runtime-only and proven in the Task-3 on-device checkpoint. THIS module holds
 * the deck's testable decisions so Vitest can assert them without a DOM:
 *   - swipe direction → keep|exclude mapping + the tap-vs-swipe slop discrimination
 *   - the Undo `prev`-capture (restore the PRIOR persisted label, never a blanket
 *     include — D-04 / RESEARCH Pitfall 5)
 *   - the optimistic label upsert that feeds deriveWorkout for live recompute (D-15)
 *   - the empty-review-set → "All clear" branch (D-10)
 *
 * Gesture constants (07-RESEARCH Pattern 1) live here as the single source so the
 * ReviewCard component and the tests agree on the thresholds.
 */
import type { LapLabel } from "@contract/types";
import { keyEquals, type CorrectionKey } from "./correctionKey";
import type { Lap } from "./laps";
import type { Suspect } from "./strokeSuspects";
import { DETECTOR_VERSION } from "./strokeSuspects";

/** Commit past this horizontal distance (px). */
export const SWIPE_THRESHOLD = 80;
/** Below this on pointerup = tap (relabel), not a swipe. */
export const TAP_SLOP = 8;

export type SwipeOutcome = "keep" | "exclude" | "tap" | "snap-back";

/**
 * Classify a pointerup by its horizontal delta. Mirrors RESEARCH Pattern 1:
 *   |dx| < TAP_SLOP        → tap (open relabel palette)
 *   |dx| >= SWIPE_THRESHOLD → commit; dx > 0 = keep (right), dx < 0 = exclude (left)
 *   otherwise              → snap-back (no commit)
 */
export function classifySwipe(dx: number): SwipeOutcome {
  const abs = Math.abs(dx);
  if (abs < TAP_SLOP) return "tap";
  if (abs >= SWIPE_THRESHOLD) return dx > 0 ? "keep" : "exclude";
  return "snap-back";
}

/** Minimum flick speed (px/ms) that commits a swipe below the distance
 *  threshold. Tuned for a deliberate flick, not a slow drag. */
export const FLICK_VELOCITY = 0.6;
/** A flick must still travel at least this far to count (filters jitter). */
export const FLICK_MIN_DX = 24;

/**
 * Resolve a release using BOTH distance and velocity. A fast flick commits in
 * its direction even below SWIPE_THRESHOLD (native card-deck feel); otherwise
 * falls back to the distance-only classifySwipe. Tap and past-threshold commits
 * are unchanged. dx>0 = keep (right), dx<0 = exclude (left) — same mapping as
 * classifySwipe, so buttons and swipes never disagree (D6 rule 4/5).
 */
export function resolveSwipe(dx: number, vx: number): SwipeOutcome {
  const byDistance = classifySwipe(dx);
  if (byDistance !== "snap-back") return byDistance; // tap or already-committed distance
  if (Math.abs(dx) >= FLICK_MIN_DX && Math.abs(vx) >= FLICK_VELOCITY) {
    return dx > 0 ? "keep" : "exclude";
  }
  return "snap-back";
}

/**
 * Tap-vs-scroll guard for bare-onclick surfaces (accidental-tap fix). A real tap
 * moves the pointer less than `slop` on BOTH axes between pointerdown and pointerup.
 * A larger movement on EITHER axis means the finger scrolled/dragged across the
 * row, so the activation must be swallowed. Defaults to the shared TAP_SLOP so the
 * row guard and the swipe deck agree on one threshold.
 */
export function isTap(
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  slop: number = TAP_SLOP,
): boolean {
  return Math.abs(endX - startX) < slop && Math.abs(endY - startY) < slop;
}

/** A committed swipe maps to whether the lap is excluded from aggregates. */
export function committedExclusion(dir: "keep" | "exclude"): boolean {
  return dir === "exclude";
}

/**
 * A label's composite identity key, or null if it predates the key fields
 * (legacy label saved before T3a). Mirrors the read-path condition in
 * strokeOverrides.ts: all three fields must be present and non-null.
 */
function labelKey(l: LapLabel): CorrectionKey | null {
  return l.offsetSeconds != null && l.durationSeconds != null && l.duplicateOrdinal != null
    ? {
        offsetSeconds: l.offsetSeconds,
        durationSeconds: l.durationSeconds,
        duplicateOrdinal: l.duplicateOrdinal,
      }
    : null;
}

/**
 * Does saved label `l` refer to the SAME physical length as the target — the lap
 * being acted on, identified by its display `lapIndex` and, when available, its
 * composite `targetKey`?
 *
 * Keyed-vs-keyed → compare by composite key, which is durable across a re-fetch
 * or lap reorder; this is what kills the lapIndex-collision clobber (C3). If
 * EITHER side lacks a key (a legacy keyless label, or no target key supplied) →
 * fall back to lapIndex equality. EXACTLY mirrors the read path's key-or-legacy
 * rule in strokeOverrides.ts / exclusions.ts.
 */
function sameTarget(l: LapLabel, lapIndex: number, targetKey: CorrectionKey | null): boolean {
  const lk = labelKey(l);
  if (lk && targetKey) return keyEquals(lk, targetKey);
  return l.lapIndex === lapIndex;
}

/**
 * Find the persisted label for a lap (the `prev` the Undo bar must restore).
 * Returns `null` when no label exists yet — i.e. the lap was on detection-default,
 * so Undo restores "no persisted label", NOT a forced include (D-04).
 *
 * Pass `targetKey` (the current lap's correctionKey) so a saved KEYED label is
 * matched by composite key, not by a stale lapIndex that a re-fetch may have
 * reassigned to a different length. Omit it for legacy lapIndex-only matching.
 */
export function priorLabelFor(
  labels: LapLabel[],
  lapIndex: number,
  targetKey: CorrectionKey | null = null,
): LapLabel | null {
  return labels.find((l) => sameTarget(l, lapIndex, targetKey)) ?? null;
}

/**
 * Optimistic upsert: return a NEW labels array with `next` applied for its
 * length (replacing any existing label for the SAME length). Pure — the caller
 * reassigns its `$state` labels array so deriveWorkout re-runs (D-15). Never
 * mutates the input (no shared-reference aliasing into the bridge payload).
 *
 * Dedup is by composite key when `next` carries one — so a correction can't
 * clobber a DIFFERENT length whose stale lapIndex happens to collide after a
 * re-fetch — falling back to lapIndex for legacy keyless labels.
 */
export function upsertLabel(labels: LapLabel[], next: LapLabel): LapLabel[] {
  const nextKey = labelKey(next);
  const out = labels.filter((l) => !sameTarget(l, next.lapIndex, nextKey));
  out.push(next);
  return out;
}

/** Merge a new decision onto the length's existing record.
 *
 *  Every writer builds a full LapLabel literal and `upsertLabel` REPLACES by target,
 *  so any field a writer omits used to be destroyed: excluding a length wiped the
 *  stroke fix you had made to it, and naming a set did the same. Merging first means
 *  a writer only overwrites what it actually decided.
 *
 *  Rule: a key the patch SETS wins (including an explicit `null` — that is how a
 *  "watch was right" confirmation legitimately clears strokeOverride). A key the patch
 *  OMITS is inherited from the prior record. So patches must omit what they do not own,
 *  never set it to undefined.
 */
export function mergeOntoPrior(prev: LapLabel | null, patch: LapLabel): LapLabel {
  if (!prev) return patch;
  return { ...prev, ...patch };
}

/**
 * Restore a prior label after Undo. If `prev` is null the lap had no persisted
 * label, so we REMOVE any label we added (back to detection-default) rather than
 * writing an include — this is the D-04 correctness point: undoing an exclude of
 * an auto-excluded wall lap must leave it auto-excluded, not force it in.
 *
 * Pass `targetKey` so the slot removed (and thus restored) is matched by
 * composite key, not a colliding lapIndex — mirrors upsertLabel.
 */
export function restorePriorLabel(
  labels: LapLabel[],
  lapIndex: number,
  prev: LapLabel | null,
  targetKey: CorrectionKey | null = null,
): LapLabel[] {
  const out = labels.filter((l) => !sameTarget(l, lapIndex, targetKey));
  if (prev) out.push(prev);
  return out;
}

/**
 * The custom-set names the deck autocompletes from — the per-user signature
 * (LABEL-01/LABEL-03/D-12). Drawn from PRIOR labels' customName values, deduped,
 * case-preserving, sorted for a stable palette. The deck only APPLIES these
 * (no create-new-set keyboard in the deck — D-12 defers entry to at-home).
 */
export function recalledCustomSets(labels: LapLabel[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const l of labels) {
    const name = l.customName?.trim();
    if (name && !seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      out.push(name);
    }
  }
  return out.sort((a, b) => a.localeCompare(b));
}

/** Should the deck render the "All clear" empty state? True when 0 review laps (D-10). */
export function isDeckEmpty(reviewLapIndices: number[]): boolean {
  return reviewLapIndices.length === 0;
}

/**
 * How long the deck waits after a commit before resolving the queued suspect
 * (F2 reduced-motion fix). Normally this is the fly-off animation's travel
 * time; under `prefers-reduced-motion` the card never visibly travels, so
 * there's nothing to wait for — commit immediately. Same commit path either
 * way (SuspectDeck's flyOff still calls accept()/watchWasRight() through this
 * single setTimeout), just a shorter delay, never a slower one.
 */
export function flyOffDelayMs(prefersReducedMotion: boolean): number {
  return prefersReducedMotion ? 0 : 200;
}

export type GestureAxis = "horizontal" | "vertical" | null;

/**
 * Decide a drag's axis once it passes `slop` on either axis. Returns null while
 * the drag is still too small to classify. A tie (|dx| === |dy|) resolves to
 * "vertical" so an ambiguous drag prefers scrolling, never an accidental back.
 * Used by the edge-swipe-back to commit to a horizontal back-drag ONLY when the
 * finger clearly moves sideways.
 */
export function lockAxis(dx: number, dy: number, slop: number = TAP_SLOP): GestureAxis {
  const ax = Math.abs(dx), ay = Math.abs(dy);
  if (ax < slop && ay < slop) return null;
  return ax > ay ? "horizontal" : "vertical";
}

/**
 * Build the label a stroke fix writes (2026-07-01 provenance decision;
 * 2026-07-05 suggestion-trace addition).
 * Provenance records HOW: picking the stroke the suspect detector suggested is
 * "suggestion-accepted" (the future auto-correct acceptance metric); any other
 * pick is "user-corrected". Preserves the prior label's exclusion decision and
 * custom name so a fix never silently erases them. Composite key stamped so
 * the label survives re-fetches (T3b); lapIndex kept for the legacy fallback.
 *
 * `suspect` carries WHAT the detector suggested and WHY at decision time —
 * training data that can't be backfilled once the detector changes. Stamped
 * regardless of whether the user accepted or overrode the suggestion.
 * `nowMs` defaults to `Date.now()`; callers pass a fixed value in tests.
 */
export function buildStrokeCorrection(
  lap: Lap,
  stroke: string,
  prev: LapLabel | null,
  suspect: Suspect | null,
  nowMs: number = Date.now(),
): LapLabel {
  return {
    lapIndex: lap.index,
    kind: "stroke-correction",
    customName: prev?.customName ?? null,
    strokeOverride: stroke,
    excludedFromAggregates: prev?.excludedFromAggregates ?? lap.effectiveExcluded,
    offsetSeconds: lap.correctionKey.offsetSeconds,
    durationSeconds: lap.correctionKey.durationSeconds,
    duplicateOrdinal: lap.correctionKey.duplicateOrdinal,
    provenance: suspect !== null && stroke === suspect.suggestedStroke
      ? "suggestion-accepted"
      : "user-corrected",
    suggestedStroke: suspect?.suggestedStroke ?? null,
    suggestionReasons: suspect ? [...suspect.reasons] : null,
    detectorVersion: suspect ? DETECTOR_VERSION : null,
    createdAtMs: nowMs,
  };
}

/**
 * Build the "watch was right" resolution label: no stroke change
 * (strokeOverride null — applyStrokeOverrides skips it), provenance
 * "user-confirmed" so the suspect detector stops flagging this length
 * (persist-until-reviewed, no re-nag). kind stays "stroke-correction":
 * the kind enum is FINALIZED (D-13) and provenance carries the meaning.
 *
 * A confirmation against a LIVE suspect records the REJECTED suggestion — the
 * negative training example ("the detector proposed X, the user said no").
 * `nowMs` defaults to `Date.now()`; callers pass a fixed value in tests.
 */
export function buildWatchConfirmation(
  lap: Lap,
  prev: LapLabel | null,
  suspect: Suspect | null,
  nowMs: number = Date.now(),
): LapLabel {
  return {
    lapIndex: lap.index,
    kind: "stroke-correction",
    customName: prev?.customName ?? null,
    strokeOverride: null,
    excludedFromAggregates: prev?.excludedFromAggregates ?? lap.effectiveExcluded,
    offsetSeconds: lap.correctionKey.offsetSeconds,
    durationSeconds: lap.correctionKey.durationSeconds,
    duplicateOrdinal: lap.correctionKey.duplicateOrdinal,
    provenance: "user-confirmed",
    suggestedStroke: suspect?.suggestedStroke ?? null,
    suggestionReasons: suspect ? [...suspect.reasons] : null,
    detectorVersion: suspect ? DETECTOR_VERSION : null,
    createdAtMs: nowMs,
  };
}
